import { useMemo, useState } from 'react';
import { Badge } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { apiGet, apiPost } from '../../lib/apiClient.js';
import { categoryKey } from '../../lib/productCatalog.js';
import type { ProductCategory } from '../../lib/types/masterData.js';

interface CategoryListResponse {
  readonly items: readonly ProductCategory[];
}

/**
 * Label-manager style category picker: type to filter the tenant's existing
 * labels and pick one, or create the label right there when it does not exist.
 * A product can carry several labels; each shows as a removable chip.
 */
export function ProductCategoryField({
  base,
  value,
  onChange,
}: {
  readonly base: string;
  readonly value: readonly string[];
  readonly onChange: (categoryIds: string[]) => void;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const list = useApiGet<CategoryListResponse>(
    `${base}/api/v1/master-data/product-categories?status=active`,
    [base, refreshKey],
  );
  const categories = list.status === 'ready' ? list.data.items : [];
  const byId = useMemo(() => new Map(categories.map((c) => [c.categoryId, c])), [categories]);

  const query = categoryKey(text);
  const selected = new Set(value);
  const suggestions = categories.filter(
    (c) => !selected.has(c.categoryId) && (query === '' || categoryKey(c.name).includes(query)),
  );
  const exact = categories.find((c) => categoryKey(c.name) === query);
  const canCreate = query !== '' && !exact;

  const add = (id: string) => {
    if (!selected.has(id)) onChange([...value, id]);
    setText('');
    setOpen(false);
  };

  async function createLabel() {
    const name = text.trim().replace(/\s+/g, ' ');
    if (!name) return;
    setBusy(true);
    setError(null);
    try {
      const created = await apiPost<ProductCategory>(
        `${base}/api/v1/master-data/product-categories`,
        { name },
      );
      setRefreshKey((k) => k + 1);
      add(created.categoryId);
    } catch {
      // Someone else may have just created the same label: reuse it.
      try {
        const again = await apiGet<CategoryListResponse>(
          `${base}/api/v1/master-data/product-categories?status=active`,
        );
        const found = again.items.find((c) => categoryKey(c.name) === categoryKey(name));
        if (found) {
          setRefreshKey((k) => k + 1);
          add(found.categoryId);
          return;
        }
      } catch {
        /* fall through to the message */
      }
      setError('ساخت دسته‌بندی ناموفق بود. دسترسی یا اتصال را بررسی کنید.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {value.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {value.map((id) => {
            const category = byId.get(id);
            return (
              <Badge
                key={id}
                tone="info"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  ...(category?.color ? { borderColor: category.color } : {}),
                }}
              >
                {category?.name ?? '…'}
                <button
                  type="button"
                  aria-label={`حذف دسته‌بندی ${category?.name ?? ''}`}
                  onClick={() => onChange(value.filter((v) => v !== id))}
                  style={{
                    background: 'none',
                    border: 0,
                    cursor: 'pointer',
                    padding: 0,
                    color: 'inherit',
                  }}
                >
                  ×
                </button>
              </Badge>
            );
          })}
        </div>
      ) : null}
      <div style={{ position: 'relative' }}>
        <input
          className="sk-input"
          value={text}
          placeholder="جستجو یا ساخت دسته‌بندی جدید…"
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault(); // Enter must not submit the product form
            if (exact && !selected.has(exact.categoryId)) add(exact.categoryId);
            else if (canCreate && !busy) void createLabel();
          }}
        />
        {open && (suggestions.length > 0 || canCreate) ? (
          <div
            role="listbox"
            className="sk-card"
            style={{
              position: 'absolute',
              insetInline: 0,
              top: '100%',
              zIndex: 20,
              maxHeight: 220,
              overflowY: 'auto',
              padding: 4,
            }}
          >
            {suggestions.map((c) => (
              <button
                key={c.categoryId}
                type="button"
                role="option"
                aria-selected={false}
                className="sk-btn sk-btn--ghost sk-btn--sm"
                style={{ width: '100%', justifyContent: 'flex-start' }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => add(c.categoryId)}
              >
                {c.name}
              </button>
            ))}
            {canCreate ? (
              <button
                type="button"
                className="sk-btn sk-btn--ghost sk-btn--sm"
                style={{ width: '100%', justifyContent: 'flex-start' }}
                disabled={busy}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => void createLabel()}
              >
                {busy ? 'در حال ساخت…' : `+ ساخت دسته‌بندی «${text.trim()}»`}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {error ? <span style={{ fontSize: 12, color: 'var(--sk-danger)' }}>{error}</span> : null}
    </div>
  );
}
